import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import { SearchableSelect } from '../../../../components/ui';
import { ANALYTICS_COPY } from '../../constants/analyticsCopy';
import { METRIC_AGGREGATIONS, aggregationNeedsMeasureField } from '../../constants/aggregations';
import { getChartConfig } from '../../utils/chartTypeMapping';
import { buildMeasureSummaryLabel, isAggregatableField } from '../../utils/measureFieldUtils';

function RecipeStep({ step, title, description, children, hideStep = false }) {
  return (
    <Stack direction="row" spacing={2} alignItems="flex-start">
      {hideStep ? null : (
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.8125rem',
          fontWeight: 700,
          flexShrink: 0,
          mt: 0.25,
        }}
      >
        {step}
      </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.25 }}>
          {title}
        </Typography>
        {description ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            {description}
          </Typography>
        ) : null}
        {children}
      </Box>
    </Stack>
  );
}

function AggregationPicker({ value, onChange, disabled, options = METRIC_AGGREGATIONS }) {
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={value}
      onChange={(_, next) => next && onChange(next)}
      disabled={disabled}
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.75,
        '& .MuiToggleButtonGroup-grouped': {
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px !important',
          mx: 0,
          px: 2,
          py: 0.75,
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.8125rem',
          flex: { xs: '1 1 calc(33% - 8px)', sm: '0 1 auto' },
          '&.Mui-selected': {
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            borderColor: 'primary.main',
            '&:hover': { bgcolor: 'primary.dark' },
          },
        },
      }}
    >
      {options.map((item) => (
        <ToggleButton key={item.id} value={item.id}>
          {item.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

function RecordsMeasureChip({ helper }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 1.25,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'grey.50',
        minHeight: 56,
      }}
    >
      <ViewColumnOutlinedIcon fontSize="small" color="action" />
      <Box>
        <Typography variant="body2" fontWeight={600}>
          {ANALYTICS_COPY.recordsMeasureLabel}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {helper || ANALYTICS_COPY.recordsMeasureHelper}
        </Typography>
      </Box>
    </Box>
  );
}

export default function AxisConfigurationPanel({
  chartType,
  dataset = '',
  metrics = [],
  groupByOptions = [],
  measureFieldOptions = [],
  timeFieldOptions = [],
  needsTableColumns = false,
  tableName = '',
  dimension,
  tableColumns = [],
  metric = '',
  measureField,
  aggregation = 'count',
  onChange,
  disabled = false,
  isLoadingFormFields = false,
  previewCategoryCount = 0,
  embedded = false,
  valueInclusion = null,
}) {
  const isWhatsApp = dataset === 'whatsapp_templates';
  const isIntervene = dataset === 'intervene';
  const usesCatalogMetrics = isWhatsApp || isIntervene;
  const recordsNumber = dataset === 'records' && chartType === 'number';
  const [advancedMeasure, setAdvancedMeasure] = useState(aggregation !== 'count');
  const config = getChartConfig(chartType);

  useEffect(() => {
    if (aggregation !== 'count') {
      setAdvancedMeasure(true);
    }
  }, [aggregation]);
  const needsMeasureField = aggregationNeedsMeasureField(aggregation);
  const timeFieldLabel = timeFieldOptions[0]?.label || 'Created date';

  const availableMeasureFields = useMemo(
    () => measureFieldOptions.filter((item) => item.id !== dimension),
    [measureFieldOptions, dimension]
  );
  const breakdownField = groupByOptions.find((item) => item.id === dimension);
  const canAggregateBreakdownField = dataset !== 'records'
    || !config.showGroupBy
    || !dimension
    || isAggregatableField(breakdownField);
  const aggregationOptions = !isLoadingFormFields
    && availableMeasureFields.length
    && canAggregateBreakdownField
    ? METRIC_AGGREGATIONS
    : METRIC_AGGREGATIONS.filter((item) => item.id === 'count');

  useEffect(() => {
    if (
      aggregation !== 'count'
      && !isLoadingFormFields
      && (!canAggregateBreakdownField
        || !availableMeasureFields.length
        || (measureField && !availableMeasureFields.some((item) => item.id === measureField)))
    ) {
      onChange({ aggregation: 'count', measureField: '' });
    }
  }, [
    aggregation,
    availableMeasureFields,
    canAggregateBreakdownField,
    isLoadingFormFields,
    measureField,
    onChange,
  ]);

  const groupByLabel = useMemo(() => {
    if (chartType === 'pie') return ANALYTICS_COPY.groupByStepLabelPie;
    if (chartType === 'table') return ANALYTICS_COPY.groupByStepLabelTable;
    return ANALYTICS_COPY.groupByStepLabel;
  }, [chartType]);

  const tableFieldsHelper = useMemo(() => {
    if (tableName) return ANALYTICS_COPY.tableFieldsFrom.replace('{table}', tableName);
    return ANALYTICS_COPY.tableFieldsGeneric;
  }, [tableName]);

  const groupByHelperText = useMemo(() => {
    if (isLoadingFormFields) return ANALYTICS_COPY.formFieldsLoading;
    if (needsTableColumns) return ANALYTICS_COPY.selectTableFirst;
    if (!groupByOptions.length && config.showGroupBy) return ANALYTICS_COPY.noTableColumns;
    if (isIntervene) return ANALYTICS_COPY.interveneGroupByHint;
    if (isWhatsApp) return ANALYTICS_COPY.whatsappGroupByHint;
    if (groupByOptions.some((option) => option.group)) return ANALYTICS_COPY.fieldsGroupedHint;
    return tableFieldsHelper;
  }, [
    isLoadingFormFields,
    needsTableColumns,
    groupByOptions,
    config.showGroupBy,
    tableFieldsHelper,
    isWhatsApp,
    isIntervene,
  ]);

  const effectiveGroupByOptions = useMemo(() => {
    if (isIntervene) {
      // For Intervene report creation, keep only the selected field (Agent (replies)), comment out others
      const selected = groupByOptions.filter((item) =>
        item.id === 'response_agent_username' ||
        item.id === dimension ||
        /agent\s*\(replies\)/i.test(item.label)
      );
      if (selected.length > 0) return selected;
      return [{ id: 'response_agent_username', label: 'Agent (replies)' }];
      /*
      // Commented out remaining Intervene group-by options per user request:
      return groupByOptions;
      */
    }
    return groupByOptions;
  }, [groupByOptions, isIntervene, dimension]);

  const selectedDimension = useMemo(() => {
    const list = isIntervene ? effectiveGroupByOptions : groupByOptions;
    const found = list.find((o) => o.id === dimension);
    if (found) return found;
    if (isIntervene && list.length > 0) return list[0];
    return null;
  }, [effectiveGroupByOptions, groupByOptions, dimension, isIntervene]);
  const categoryLabel = selectedDimension?.label || '';
  const coverageHint = selectedDimension?.partial
    ? ANALYTICS_COPY.partialFieldHint
      .replace('{field}', selectedDimension.label)
      .replace('{tables}', (selectedDimension.forms || []).map((form) => form.label).filter(Boolean).join(', '))
    : '';
  const measureFieldLabel = availableMeasureFields.find((o) => o.id === measureField)?.label || '';
  const summaryLabel = buildMeasureSummaryLabel({
    aggregation,
    measureFieldLabel,
    categoryLabel: config.showGroupBy ? categoryLabel : timeFieldLabel,
  });

  const selectedMeasure = availableMeasureFields.find((item) => item.id === measureField) || null;
  const measureHelper = useMemo(() => {
    if (aggregation === 'count') return null;
    if (!availableMeasureFields.length) return ANALYTICS_COPY.noNumericFields;
    if (!measureField) return ANALYTICS_COPY.selectMeasureFieldHelper;
    if (selectedMeasure?.partial) {
      const tables = (selectedMeasure.forms || []).map((form) => form.label).filter(Boolean).join(', ');
      return ANALYTICS_COPY.partialFieldHint
        .replace('{field}', selectedMeasure.label)
        .replace('{tables}', tables);
    }
    return ANALYTICS_COPY.aggregationFieldHelper.replace('{field}', measureFieldLabel);
  }, [aggregation, measureField, measureFieldLabel, availableMeasureFields.length, selectedMeasure]);

  if (needsTableColumns && config.showGroupBy) {
    return (
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        {ANALYTICS_COPY.selectTableFirst}
      </Alert>
    );
  }

  const metricOptions = useMemo(() => {
    const list = metrics.map((item) => ({ id: item.id, label: item.label }));
    if (isIntervene) {
      // For Intervene report creation, keep only the selected metric (Avg agent response time), comment out others
      const selected = list.filter((item) =>
        item.id === 'avg_agent_response' ||
        item.id === 'agent_response_time' ||
        item.id === metric ||
        /avg.*agent.*response/i.test(item.label)
      );
      if (selected.length > 0) return selected;
      return [{ id: 'avg_agent_response', label: 'Avg agent response time' }];
      /*
      // Commented out other metrics per user request:
      if (!list.some((item) => item.id === 'agent_response_time')) {
        list.unshift({ id: 'agent_response_time', label: 'Agent response time' });
      }
      return list;
      */
    }
    return list;
  }, [metrics, isIntervene, metric]);

  const selectedMetric = useMemo(() => {
    const found = metricOptions.find((item) => item.id === metric);
    if (found) return found;
    if (isIntervene && metricOptions.length > 0) {
      return metricOptions[0];
    }
    if (metric === 'agent_response_time') {
      return { id: 'agent_response_time', label: 'Agent response time' };
    }
    return null;
  }, [metricOptions, metric, isIntervene]);

  const renderCalculateStep = () => {
    if (recordsNumber && !advancedMeasure) {
      return (
        <Stack spacing={0.75} alignItems="flex-start">
          <Typography variant="subtitle2" fontWeight={700}>
            {ANALYTICS_COPY.calculateStepLabel}
          </Typography>
          <Typography variant="body2">
            {ANALYTICS_COPY.recordsMeasureLabel}
          </Typography>
          <Button
            variant="text"
            onClick={() => setAdvancedMeasure(true)}
            disabled={disabled}
            sx={{ textTransform: 'none', fontWeight: 600, px: 0.5 }}
          >
            {ANALYTICS_COPY.calculateFromColumn}
          </Button>
        </Stack>
      );
    }

    return (
    <RecipeStep
      hideStep={recordsNumber}
      step={config.showGroupBy || config.showTrendInfo ? 2 : 1}
      title={ANALYTICS_COPY.calculateStepLabel}
      description={
        isWhatsApp
          ? ANALYTICS_COPY.whatsappShowDescription
          : isIntervene
            ? ANALYTICS_COPY.interveneShowDescription
            : recordsNumber
              ? ''
              : ANALYTICS_COPY.calculateStepDescription
      }
    >
      <Stack spacing={2}>
        {usesCatalogMetrics ? (
          <SearchableSelect
            label={ANALYTICS_COPY.metricLabel}
            placeholder={ANALYTICS_COPY.metricLabel}
            options={metricOptions}
            value={selectedMetric}
            onChange={(opt) => {
              const nextMetric = opt?.id || '';
              if (isIntervene && nextMetric === 'agent_response_time') {
                const agentDim = groupByOptions.find((d) =>
                  ['response_agent_username', 'agent_username', 'agent_name', 'agent_user_id', 'agent'].includes(d.id)
                )?.id || 'response_agent_username';
                onChange({
                  metric: nextMetric,
                  dimension: dimension || agentDim,
                  chartType: chartType === 'number' ? 'bar' : chartType,
                });
                return;
              }
              onChange({ metric: nextMetric });
            }}
            getOptionLabel={(opt) => opt.label}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            disabled={disabled || !metricOptions.length}
            clearable={false}
          />
        ) : (
          <>
        {recordsNumber ? (
          <Button
            variant="text"
            onClick={() => {
              setAdvancedMeasure(false);
              onChange({ aggregation: 'count', measureField: '' });
            }}
            disabled={disabled}
            sx={{ textTransform: 'none', fontWeight: 600, px: 0.5, alignSelf: 'flex-start' }}
          >
            {ANALYTICS_COPY.useRecordCountInstead}
          </Button>
        ) : null}
        <AggregationPicker
          value={aggregation}
          disabled={disabled}
          options={aggregationOptions}
          onChange={(next) => onChange({
            aggregation: next,
            measureField: next === 'count' ? '' : measureField,
          })}
        />
        {needsMeasureField ? (
          <SearchableSelect
            label={ANALYTICS_COPY.measureFieldLabel}
            placeholder={ANALYTICS_COPY.measureFieldPlaceholder}
            options={availableMeasureFields}
            value={availableMeasureFields.find((o) => o.id === measureField) || null}
            onChange={(opt) => onChange({ measureField: opt?.id || '' })}
            getOptionLabel={(opt) => opt.label}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            groupBy={availableMeasureFields.some((option) => option.group) ? (option) => option.group : undefined}
            disabled={disabled || !availableMeasureFields.length}
            loading={isLoadingFormFields}
            required
            helperText={measureHelper || undefined}
            error={Boolean(measureHelper && !measureField && availableMeasureFields.length)}
          />
        ) : (
          <RecordsMeasureChip
            helper={chartType === 'line' ? ANALYTICS_COPY.recordsMeasureHelperLine : undefined}
          />
        )}
          </>
        )}
      </Stack>
    </RecipeStep>
    );
  };

  const summaryBlock = !embedded && summaryLabel && (config.showGroupBy || config.showTrendInfo || chartType === 'number') ? (
    <Box
      sx={(theme) => ({
        px: embedded ? 0 : { xs: 2, sm: 2.5 },
        py: 2,
        mt: embedded ? 2 : 0,
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: alpha(theme.palette.primary.main, 0.06),
        borderRadius: embedded ? 1.5 : 0,
      })}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <CalculateOutlinedIcon fontSize="small" color="primary" />
        <Typography variant="caption" color="primary.main" fontWeight={700} letterSpacing={0.5}>
          {ANALYTICS_COPY.reportPreviewLabel}
        </Typography>
      </Stack>
      <Typography variant="h6" fontWeight={700} lineHeight={1.35} color="text.primary">
        {summaryLabel}
      </Typography>
    </Box>
  ) : null;

  const recipeContent = (
    <>
      <Stack spacing={3} sx={{ p: embedded ? 0 : { xs: 2, sm: 2.5 } }}>
        {config.showGroupBy ? (
          <RecipeStep
            step={1}
            title={chartType === 'table' && !usesCatalogMetrics ? ANALYTICS_COPY.tableColumnsLabel : groupByLabel}
            description={
              chartType === 'table' && !usesCatalogMetrics
                ? ANALYTICS_COPY.tableColumnsHint
                : isWhatsApp
                  ? ANALYTICS_COPY.whatsappGroupByDescription
                  : isIntervene
                    ? ANALYTICS_COPY.interveneGroupByDescription
                    : ANALYTICS_COPY.groupByStepDescription
            }
          >
            {chartType === 'table' && !usesCatalogMetrics ? (
              <SearchableSelect
                multiple
                label={ANALYTICS_COPY.tableColumnsLabel}
                placeholder={ANALYTICS_COPY.tableColumnsPlaceholder}
                options={groupByOptions}
                value={groupByOptions.filter((option) => (
                  (tableColumns.length ? tableColumns : (dimension ? [dimension] : [])).includes(option.id)
                ))}
                onChange={(opts) => {
                  const ids = (opts || []).map((option) => option.id);
                  onChange({
                    tableColumns: ids,
                    dimension: ids[0] || '',
                    includedValues: [],
                  });
                }}
                getOptionLabel={(opt) => opt.label}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                groupBy={groupByOptions.some((option) => option.group) ? (option) => option.group : undefined}
                disabled={disabled || !groupByOptions.length}
                loading={isLoadingFormFields}
                required
              />
            ) : (isIntervene ? effectiveGroupByOptions.length : groupByOptions.length) ? (
              <SearchableSelect
                label={ANALYTICS_COPY.groupByFieldLabel}
                placeholder={ANALYTICS_COPY.groupByFieldPlaceholder}
                options={effectiveGroupByOptions}
                value={selectedDimension}
                onChange={(opt) => onChange({ dimension: opt?.id || '' })}
                getOptionLabel={(opt) => opt.label}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                groupBy={effectiveGroupByOptions.some((option) => option.group) ? (option) => option.group : undefined}
                disabled={disabled}
                loading={isLoadingFormFields}
                helperText={coverageHint || groupByHelperText}
              />
            ) : (
              <Alert severity="warning" sx={{ borderRadius: 1.5 }}>
                {isLoadingFormFields ? ANALYTICS_COPY.formFieldsLoading : ANALYTICS_COPY.noTableColumns}
              </Alert>
            )}
          </RecipeStep>
        ) : null}

        {config.showGroupBy && dimension ? valueInclusion : null}

        {config.showTrendInfo ? (
          <RecipeStep
            step={1}
            title={ANALYTICS_COPY.groupByStepLabelTime}
            description={
              isWhatsApp
                ? ANALYTICS_COPY.whatsappTrendDescription
                : isIntervene
                  ? ANALYTICS_COPY.interveneTrendDescription
                  : ANALYTICS_COPY.groupByStepDescriptionTime
            }
          >
            {usesCatalogMetrics ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 2,
                  py: 1.25,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'grey.50',
                }}
              >
                <TimelineOutlinedIcon color="primary" fontSize="small" />
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {timeFieldLabel}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {isWhatsApp
                      ? ANALYTICS_COPY.whatsappTrendDescription
                      : ANALYTICS_COPY.interveneTrendDescription}
                  </Typography>
                </Box>
              </Box>
            ) : null}
          </RecipeStep>
        ) : null}

        {config.showMeasure && !(chartType === 'table' && !usesCatalogMetrics) ? renderCalculateStep() : null}

        {chartType === 'pie' && previewCategoryCount > 8 ? (
          <Alert severity="warning" sx={{ borderRadius: 1.5 }}>
            {ANALYTICS_COPY.pieTooManyCategories}
          </Alert>
        ) : null}
      </Stack>
      {summaryBlock}
    </>
  );

  if (embedded) {
    return recipeContent;
  }

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50',
        }}
      >
        <Typography variant="subtitle1" fontWeight={700}>
          {ANALYTICS_COPY.reportRecipeTitle}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {ANALYTICS_COPY.reportRecipeSubtitle}
        </Typography>
      </Box>
      {recipeContent}
    </Box>
  );
}
